import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { roundMoney, allocateProportionally } from './transportCostAllocation';

export const SPLIT_QUANTITY_PRECISION = 3;

export interface TransportSplitProduct {
  key: string;
  orderId?: string;
  orderProductId?: string;
  productName: string;
  quantity: number;
  unit?: string;
  goodsValueTotal?: number | null;
  grossWeight?: number;
  width?: number;
  height?: number;
  length?: number;
  thickness?: number;
  // Package-based LDM (logistics) snapshot per split product line.
  piecesPerPackage?: number | null;
  packageLength?: number | null;
  packageWidth?: number | null;
  packageStackLayers?: number | null;
}

export interface TransportSplitRow {
  key: string;
  quantities: Record<string, number>;
  transportRate?: number | null;
  currency?: string;
  loadingDate?: Dayjs | null;
}

export interface SplitTruckCargoItem {
  orderId?: string;
  orderProductId?: string;
  productName: string;
  quantity: number;
  unit?: string;
  goodsValueTotal: number;
  width?: number;
  height?: number;
  length?: number;
  thickness?: number;
  grossWeight?: number;
  // Package-based LDM (logistics) snapshot forwarded to each truck.
  piecesPerPackage?: number | null;
  packageLength?: number | null;
  packageWidth?: number | null;
  packageStackLayers?: number | null;
}

export interface SplitTruckPayload {
  transportRate: number | null;
  currency?: string;
  loadingDate?: string;
  cargoItems: SplitTruckCargoItem[];
}

function toNumber(value: unknown): number {
  if (value === null || value === undefined) {
    return 0;
  }

  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
}

function roundToPrecision(value: number, precision: number): number {
  const factor = 10 ** precision;
  const rounded = Math.round(value * factor) / factor;
  return rounded === 0 ? 0 : rounded;
}

export function roundSplitQuantity(value: number): number {
  return roundToPrecision(value, SPLIT_QUANTITY_PRECISION);
}

export function equalSplit(total: number, parts: number, precision = SPLIT_QUANTITY_PRECISION): number[] {
  const normalizedParts = Math.floor(Number(parts));
  const normalizedPrecision = Math.max(Math.floor(Number(precision)), 0);
  const normalizedTotal = toNumber(total);

  if (normalizedParts <= 0) {
    return [];
  }

  if (normalizedParts === 1) {
    return [roundToPrecision(normalizedTotal, normalizedPrecision)];
  }

  const factor = 10 ** normalizedPrecision;
  const baseShare = Math.floor((normalizedTotal / normalizedParts) * factor) / factor;
  const shares = Array.from({ length: normalizedParts - 1 }, () => baseShare);
  const allocatedBeforeRemainder = baseShare * (normalizedParts - 1);

  return [
    ...shares,
    roundToPrecision(normalizedTotal - allocatedBeforeRemainder, normalizedPrecision),
  ];
}

export function proportionalShare(totalValue: number, partQty: number, totalQty: number): number {
  const normalizedTotalQty = toNumber(totalQty);

  if (normalizedTotalQty <= 0) {
    return 0;
  }

  return roundMoney(toNumber(totalValue) * (toNumber(partQty) / normalizedTotalQty));
}

// Per-product, per-row resolved quantity + goods value, keyed by row index. Only
// rows whose ROUNDED quantity survives (> 0) appear, so the remainder always
// lands on a row that is actually emitted.
interface ResolvedProductSplit {
  quantity: number;
  goodsValueTotal: number;
}

// Resolve one product's split across all rows so that the SUM of the emitted
// (rounded, surviving) quantities equals the rounded raw total, and the SUM of
// the emitted goods values equals the product's total goods value — both exactly.
//
// Cumulative-rounding drives the quantities: rounded_i = round(cumRaw_i) -
// round(cumRaw_{i-1}). Rows whose resulting rounded quantity is <= 0 are dropped
// (not loaded on that truck). The quantity remainder is inherent to cumulative
// rounding; the goods value is split through the shared allocateProportionally
// (cumulative-rounding / largest-remainder), which sums EXACTLY to the rounded
// total with every share >= 0 — no remainder-on-last-row money copy that could
// go negative sub-cent.
function resolveProductSplit(
  rows: TransportSplitRow[],
  product: TransportSplitProduct,
): Map<number, ResolvedProductSplit> {
  const rawQuantities = rows.map((row) => Math.max(Number(row.quantities[product.key] || 0), 0));

  // Pass 1: cumulative-rounded quantities; keep only rows that survive (> 0).
  let cumulativeRaw = 0;
  let previousRounded = 0;
  const roundedByRow: number[] = rawQuantities.map((rawQuantity) => {
    cumulativeRaw += rawQuantity;
    const cumulativeRounded = roundSplitQuantity(cumulativeRaw);
    const rounded = roundSplitQuantity(cumulativeRounded - previousRounded);
    previousRounded = cumulativeRounded;
    return rounded;
  });

  const survivingRowIndexes = roundedByRow.reduce<number[]>((indexes, quantity, index) => {
    if (quantity > 0) {
      indexes.push(index);
    }
    return indexes;
  }, []);

  // Pass 2: split goods value across the surviving rows through the shared
  // allocateProportionally, weighted by the surviving rounded quantities. The
  // helper's cumulative-rounding sums to roundMoney(totalGoodsValue) exactly and
  // never emits a negative share, so no value is lost when trailing tiny rows
  // round away and no last-row remainder can dip below zero.
  const totalGoodsValue = Number(product.goodsValueTotal || 0);
  const survivingQuantities = survivingRowIndexes.map((index) => roundedByRow[index]);
  const goodsValueShares = allocateProportionally(totalGoodsValue, survivingQuantities);
  const resolved = new Map<number, ResolvedProductSplit>();

  survivingRowIndexes.forEach((index, position) => {
    resolved.set(index, {
      quantity: roundedByRow[index],
      goodsValueTotal: goodsValueShares[position] || 0,
    });
  });

  return resolved;
}

export function buildSplitTrucks(
  rows: TransportSplitRow[],
  products: TransportSplitProduct[],
): SplitTruckPayload[] {
  const resolvedByProductKey = new Map<string, Map<number, ResolvedProductSplit>>();
  products.forEach((product) => {
    resolvedByProductKey.set(product.key, resolveProductSplit(rows, product));
  });

  return rows.map((row, rowIndex) => {
    const cargoItems = products
      .map((product): SplitTruckCargoItem | null => {
        const resolved = resolvedByProductKey.get(product.key)?.get(rowIndex);
        if (!resolved) {
          return null;
        }

        return {
          orderId: product.orderId,
          orderProductId: product.orderProductId,
          productName: product.productName,
          quantity: resolved.quantity,
          unit: product.unit,
          goodsValueTotal: resolved.goodsValueTotal,
          width: product.width,
          height: product.height,
          length: product.length,
          thickness: product.thickness,
          grossWeight: product.grossWeight,
          piecesPerPackage: product.piecesPerPackage ?? null,
          packageLength: product.packageLength ?? null,
          packageWidth: product.packageWidth ?? null,
          packageStackLayers: product.packageStackLayers ?? null,
        };
      })
      .filter((item): item is SplitTruckCargoItem => Boolean(item));

    const truck: SplitTruckPayload = {
      transportRate: row.transportRate ?? null,
      currency: row.currency,
      cargoItems,
    };

    if (dayjs.isDayjs(row.loadingDate)) {
      truck.loadingDate = row.loadingDate.format('YYYY-MM-DD');
    }

    return truck;
  }).filter((truck) => truck.cargoItems.length > 0);
}
